import {Button, Menu} from '@mantine/core'
import {IconChevronDown} from '@tabler/icons-react'

import {lifecycleStatusColor, lifecycleStatusLabel} from '../theme/semantic-colors'
import type {ProjectLifecycleStatus} from './schemas'

type ProjectStatusControlProps = {
    status: ProjectLifecycleStatus
    pending: boolean
    onApprove: () => void
    onArchive: () => void
    onReopen: () => void
}

type StatusMenuAction = {
    key: string
    label: string
    color?: string
    onClick: () => void
}

function menuActionsFor(
    status: ProjectLifecycleStatus,
    handlers: Pick<ProjectStatusControlProps, 'onApprove' | 'onArchive' | 'onReopen'>,
): StatusMenuAction[] {
    switch (status) {
        case 'estimate':
            return [
                {key: 'approve', label: 'Approve to start', onClick: handlers.onApprove},
                {key: 'archive', label: 'Archive', color: 'red', onClick: handlers.onArchive},
            ]
        case 'in_progress':
            return [{key: 'archive', label: 'Archive', color: 'red', onClick: handlers.onArchive}]
        case 'closed':
            return [{key: 'reopen', label: 'Reopen', onClick: handlers.onReopen}]
    }
}

/**
 * Single status pill/menu replacing the project detail page's separate
 * Approve/Archive/Reopen buttons. The menu only ever offers the transitions
 * reachable from the current status.
 */
export function ProjectStatusControl({
    status,
    pending,
    onApprove,
    onArchive,
    onReopen,
}: ProjectStatusControlProps) {
    const actions = menuActionsFor(status, {onApprove, onArchive, onReopen})
    const statusLabel = lifecycleStatusLabel(status)

    return (
        <Menu position="bottom-end" withinPortal>
            <Menu.Target>
                <Button
                    variant="light"
                    color={lifecycleStatusColor(status)}
                    size="compact-lg"
                    rightSection={<IconChevronDown size={14} stroke={1.5} />}
                    loading={pending}
                    aria-label={`Order status: ${statusLabel}. Open to change status.`}
                >
                    {statusLabel}
                </Button>
            </Menu.Target>
            <Menu.Dropdown>
                <Menu.Label>Order status</Menu.Label>
                {actions.map((action) => (
                    <Menu.Item
                        key={action.key}
                        color={action.color}
                        disabled={pending}
                        onClick={action.onClick}
                    >
                        {action.label}
                    </Menu.Item>
                ))}
            </Menu.Dropdown>
        </Menu>
    )
}
